import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Redirect, Link } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import ClipLoader from 'react-spinners/ClipLoader';

import {
  fetchEntities,
  createEntity,
  updateEntity,
  archiveEntity,
} from 'state/actions/stations';
import {
  fetchEntities as fetchSatellites,
  entitiesCleanUp as cleanupSatellites,
} from 'state/actions/satellites';
import {
  fetchEntities as fetchStationsUsersGroups,
  entitiesCleanUp as cleanupStationsUsersGroups,
} from 'state/actions/stationsUsersGroups';

import { createEntity as createEntityLog } from 'state/actions/logs';
// import { createUser, modifyUser, fetchEntities } from 'state/actions/stations';
import CONSTANTS from 'utils/constants';
import { useFormatMessage } from 'hooks';
import ConfirmationModal from 'components/ConfirmationModal';

import StationForm from './StationForm';
import StationProfile from './StationProfile';
import StationInstallation from './StationInstallation';
import { yupSchema, getSatelliteKeys } from './schema';

const EntityForm = StationForm;

export default () => {
  const {
    i18nEntityName,
    entitiesName,
    PATH_ENTITIES,
  } = CONSTANTS.ENTITIES.STATIONS;

  const { id, action } = useParams();

  const isEditing = useMemo(() => !!id && action === 'edit', [id, action]);
  const isProfile = useMemo(() => !!id && action === 'profile', [id, action]);
  const isInstalling = useMemo(() => !!id && action === 'install', [
    id,
    action,
  ]);
  const isCreating = useMemo(() => !id && !action, [id, action]);

  const {
    isAdmin,
    isInstaller,
    success,
    entityData = {},
    error,
    loading,
    archived,
    satellites = [],
    stationsUsersGroups = [],
  } = useSelector(
    (state) => ({
      isAdmin: CONSTANTS.IS_USER_ROLE_ADMIN[state.auth.userData.role],
      isInstaller: CONSTANTS.IS_USER_ROLE_INSTALLER[state.auth.userData.role],

      success: state[entitiesName].success,
      entityData: state[entitiesName].data.find(
        (entity) => entity && entity.id === id
      ),
      error: state[entitiesName].error,
      loading: state[entitiesName].loading,
      archived: state[entitiesName].archived,
      satellites: state.satellites.data.sort((a, b) => {
        const nameA = a.name;
        const nameB = b.name;
        if (nameA === nameB) {
          return 0;
        }
        return nameA > nameB;
      }),
      stationsUsersGroups: state.stationsUsersGroups.data,
    }),
    shallowEqual
  );

  const [archiveModal, setArchiveModal] = useState({
    entityId: null,
    isOpen: false,
  });

  const [currentStationsUsersGroups, setCurrentStationsUsersGroups] = useState(
    []
  );

  useEffect(() => {
    if (archived && !loading) {
      setArchiveModal((prevState) => ({
        entityId: null,
        isOpen: !prevState.isOpen,
      }));
    }
  }, [archived, loading]);

  const onCloseModalHandler = () => {
    setArchiveModal({ entityId: null, isOpen: false });
  };

  const dispatch = useDispatch();

  // console.log({ id, entityData });
  useEffect(() => {
    if (isEditing || isInstalling) {
      if (!entityData) {
        dispatch(fetchEntities(id));
      }
    }
  }, [isEditing, isInstalling, id, entityData, dispatch]);

  // Fetch satellites
  useEffect(() => {
    dispatch(fetchSatellites());
    return () => {
      dispatch(cleanupSatellites());
    };
  }, []);

  // Fetch StationsUsersGroups
  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchStationsUsersGroups());
    }
    return () => {
      dispatch(cleanupStationsUsersGroups());
    };
  }, []);

  // Filter groups to keep only those that the station belongs to
  useEffect(() => {
    if (stationsUsersGroups && stationsUsersGroups.length > 0) {
      const groupsForStation = stationsUsersGroups.filter((group) => {
        return group && group.stationsIds && group.stationsIds.includes(id);
      });
      setCurrentStationsUsersGroups(groupsForStation);
    } else {
      setCurrentStationsUsersGroups([]);
    }
  }, stationsUsersGroups);

  const editEntityMessage = useFormatMessage(`${i18nEntityName}.edit`);

  const newEntityMessage = useFormatMessage(`${i18nEntityName}.create`);
  const installEntityTitle = useFormatMessage(`${i18nEntityName}.install`);

  const onSubmitHandler = (value) => {
    const newEntity = {
      ...value,
    };
    if (id) {
      newEntity.id = id;
    }
    const options = {};

    // If admin, Process StationsUsersGroups changes
    if (isAdmin) {
      const addedCurrentStationsUsersGroupsIds = [];
      const removedCurrentStationsUsersGroupsIds = [];

      // 1. First, create array with only ids
      const currentStationsUsersGroupsIds = (
        currentStationsUsersGroups || []
      ).map((i) => i && i.id);

      const selectedStationsUsersGroupsIds = (
        newEntity.stationsUsersGroups || []
      ).filter((i) => i);

      // 2. Remove stationId where station does not appear anymore
      currentStationsUsersGroups.forEach((group) => {
        const groupId = group.id;
        if (!selectedStationsUsersGroupsIds.includes(groupId)) {
          removedCurrentStationsUsersGroupsIds.push(groupId);
        }
      });

      // 3. Add stationId where station appear but were not present beafore
      selectedStationsUsersGroupsIds.forEach((groupId) => {
        if (!currentStationsUsersGroupsIds.includes(groupId)) {
          addedCurrentStationsUsersGroupsIds.push(groupId);
        }
      });

      // Delete selectedStationsUsersGroups attribute
      delete newEntity.stationsUsersGroups;

      options.stationsUsersGroupsIdsToAdd = addedCurrentStationsUsersGroupsIds;
      options.stationsUsersGroupsIdsToRemove = removedCurrentStationsUsersGroupsIds;
    }
    if (isEditing) {
      dispatch(updateEntity(newEntity, options));
    } else if (isInstalling) {
      const installedEntity = {
        // As installation does not fill all fields
        // Take them from original data
        ...entityData,
        // And fill new ones
        id: newEntity.id,
        status: CONSTANTS.STATIONS_STATUS_ENUM.INSTALLED,
        installedAt: new Date().toISOString(),
      };
      // Complete with satellite ones
      getSatelliteKeys().forEach((key) => {
        installedEntity[key] = value[key];
      });
      dispatch(updateEntity(installedEntity, options));
    } else {
      dispatch(createEntity(newEntity, options));
    }
  };

  const onActivityLogSubmitHandler = (value) => {
    const newLog = { ...value, createdAt: new Date().toISOString() };
    dispatch(createEntityLog(newLog));
  };

  let title = newEntityMessage;
  if (isEditing) {
    title = editEntityMessage;
  } else if (isInstalling) {
    title = installEntityTitle;
  } else if (isProfile) {
    title = (
      <span>
        <span className="has-text-weight-normal">
          {useFormatMessage(`${i18nEntityName}.titlePrefix`)}
        </span>
        &nbsp;{entityData.name}
      </span>
    );
  }

  const redirect = isEditing && error && <Redirect to={PATH_ENTITIES} />;
  const redirectSuccess = (isEditing || isInstalling) && success && (
    <Redirect to={`${PATH_ENTITIES}/${entityData.id}/profile`} />
  );
  const redirectArchived = isProfile && archived && (
    <Redirect to={`${PATH_ENTITIES}`} />
  );
  const redirectCreatingSuccess = isCreating && success && (
    <Redirect to={PATH_ENTITIES} />
  );

  const onRemoveButtonClickHandler = (entityId) => {
    setArchiveModal((prevState) => ({
      entityId,
      isOpen: !prevState.isOpen,
    }));
  };

  const onDeleteEntityHandler = () => {
    dispatch(archiveEntity(archiveModal.entityId));
  };

  const archiveMessage = useFormatMessage(`${i18nEntityName}.archive`);
  const confirmMessage = useFormatMessage(`${i18nEntityName}.confirm`);
  const permArchiveMessage = useFormatMessage(`${i18nEntityName}.permArchive`);
  const cancelMessage = useFormatMessage(`${i18nEntityName}.cancel`);

  const options = {
    satellites,
    stationsUsersGroups,
    currentStationsUsersGroups,
  };

  const entityStatus = entityData && entityData.status;
  let canUserEdit = false;
  if (isAdmin) {
    canUserEdit = true;
  } else if (isInstaller) {
    canUserEdit = CONSTANTS.STATIONS_STATUS_FOR_INSTALLER_TO_WRITE.includes(
      entityStatus
    );
  }

  return (
    <>
      {redirect}
      {redirectSuccess}
      {redirectCreatingSuccess}
      {redirectArchived}
      {archiveModal.isOpen && (
        <ConfirmationModal
          isActive={archiveModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={archiveMessage}
          title={confirmMessage}
          body={permArchiveMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteEntityHandler}
          onCancel={onCloseModalHandler}
        />
      )}

      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">{title}</h1>
              </div>
            </div>
            {isProfile && canUserEdit && (isInstaller || isAdmin) && (
              <div className="level-right">
                <div className="level-item">
                  <Link
                    to={`/${entitiesName}/${entityData.id}/edit`}
                    className="button"
                  >
                    {useFormatMessage(`${i18nEntityName}.edit`)}
                  </Link>
                </div>{' '}
                <div className="level-item">
                  <Link
                    to={`/${entitiesName}/${entityData.id}/install`}
                    className="button"
                  >
                    {useFormatMessage(`${i18nEntityName}.install`)}
                  </Link>
                </div>{' '}
                {isAdmin && (
                  <div className="level-item">
                    <button
                      type="button"
                      className="button is-warning"
                      onClick={() => onRemoveButtonClickHandler(id)}
                    >
                      {/* <span className="icon is-small">
                      <i className="mdi mdi-trash-can" />
                    </span> */}
                      {useFormatMessage(`${i18nEntityName}.archive`)}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        {isProfile && (
          <StationProfile
            entity={entityData}
            onActivityLogSubmitHandler={onActivityLogSubmitHandler}
            options={{ ...options, isAdmin }}
          />
        )}

        {isEditing && canUserEdit && !entityData && <ClipLoader />}
        {(isEditing || isCreating) && canUserEdit && (
          <EntityForm
            isEditing={isEditing}
            isCreating={isCreating}
            entity={isEditing ? entityData : yupSchema.getDefault()}
            onSubmitHandler={onSubmitHandler}
            schema={yupSchema}
            options={options}
          />
        )}

        {isInstalling && canUserEdit && !entityData && <ClipLoader />}
        {isInstalling && canUserEdit && (
          <StationInstallation
            entity={entityData}
            schema={yupSchema}
            options={options}
            onSubmitHandler={onSubmitHandler}
          />
        )}
      </section>
    </>
  );
};
